import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  useEffect(() => {
    if (!token) setError(t("common.error"));
  }, [token, t]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(pw1)) { setError(t("auth.passwordStrength")); return; }
    if (pw1 !== pw2) { setError(t("settings.password.confirm")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(errorMessage(err, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="card-base p-8">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 size={48} className="mx-auto text-green-600 mb-3" />
              <h1 className="font-display text-2xl font-bold text-worker">{t("auth.resetSuccess")}</h1>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold text-worker">{t("auth.resetTitle")}</h1>
              <p className="mt-2 text-smoke text-sm">{t("auth.resetSubtitle")}</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="label-base">{t("settings.password.new")}</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" />
                    <input
                      type={showPw1 ? "text" : "password"} required minLength={8}
                      value={pw1} onChange={(e) => setPw1(e.target.value)}
                      className="input-base pl-10 pr-10" placeholder="••••••••"
                      data-testid="new-password-input"
                    />
                    <button type="button" onClick={() => setShowPw1(!showPw1)} className="absolute right-3 top-1/2 -translate-y-1/2 text-smoke hover:text-worker" data-testid="new-pw-toggle" tabIndex={-1}>
                      {showPw1 ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="label-base">{t("settings.password.confirm")}</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" />
                    <input
                      type={showPw2 ? "text" : "password"} required minLength={8}
                      value={pw2} onChange={(e) => setPw2(e.target.value)}
                      className="input-base pl-10 pr-10" placeholder="••••••••"
                      data-testid="confirm-password-input"
                    />
                    <button type="button" onClick={() => setShowPw2(!showPw2)} className="absolute right-3 top-1/2 -translate-y-1/2 text-smoke hover:text-worker" data-testid="confirm-pw-toggle" tabIndex={-1}>
                      {showPw2 ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-sm p-2 text-sm text-red-700" data-testid="reset-error">
                    <AlertCircle size={14} className="inline mr-1" /> {error}
                  </div>
                )}
                <button type="submit" disabled={loading || !token} className="fx-btn fx-btn--primary w-full" data-testid="reset-submit">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : null}
                  {t("auth.resetBtn")}
                </button>
              </form>
              <p className="mt-4 text-center text-xs text-smoke">
                <Link to="/login" className="hover:text-worker">{t("auth.backToLogin")}</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
